import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { InstructionDialogConfig } from '~/components/Instructions/useInstructionDialog';
import { useGetContentStrings } from '~/data/contentStrings';
import {
  OverlappingEmploymentFormType,
  OverlappingEmploymentFormValues,
} from '~/features/OverlappingEmploymentDialog/hooks/useOverlappingEmploymentForm';
import { useDialogState } from '~/hooks/useDialogState';
import { getCurrentLanguage } from '~/localization';

export enum OverlappingDialogTabs {
  BasicData = 'basicData',
  Contact = 'contact',
  CustomData = 'customData',
  EmploymentDetails = 'employmentDetails',
  Rewarding = 'rewarding',
}

export const useOverlappingDialogTabs = (
  formValues: OverlappingEmploymentFormValues,
) => {
  const {
    formProps: { control, trigger },
  } = formValues;

  const { t } = useTranslation();

  const currentLang = getCurrentLanguage();
  const { isLoading, data: instructionStrings } = useGetContentStrings(
    {
      contentKeys: [
        'INSTRUCTIONS_NEW_EMP_BASIC',
        'INSTRUCTIONS_NEW_EMP_CONTACT',
        'INSTRUCTIONS_NEW_EMP_CUSTOM',
        'INSTRUCTIONS_NEW_EMP_DETAILS',
        'INSTRUCTIONS_NEW_EMP_REWARDING',
      ],
      langCode: currentLang,
    },
    { revalidateOnFocus: false, revalidateIfStale: false, refreshInterval: 0 },
  );

  const confirmationDialogState = useDialogState();

  // Tab states & state updating
  const [activeTab, setActiveTab] = useState<OverlappingDialogTabs>(
    OverlappingDialogTabs.BasicData,
  );

  // - Enabled tabs: User can access tab
  const [enabledTabs, setEnabledTabs] = useState<OverlappingDialogTabs[]>([
    OverlappingDialogTabs.BasicData,
  ]);
  const updateEnabledTabs = useCallback(
    (tab: OverlappingDialogTabs) => {
      if (!enabledTabs.includes(tab)) {
        setEnabledTabs([...enabledTabs, tab]);
      }
    },
    [enabledTabs, setEnabledTabs],
  );

  // - Validate tabs: Form should run validation on tab
  const [validateTabs, setValidateTabs] = useState<OverlappingDialogTabs[]>([]);
  const updateValidateTabs = useCallback(
    (tab: OverlappingDialogTabs) => {
      if (!validateTabs.includes(tab)) {
        setValidateTabs([...validateTabs, tab]);
      }
    },
    [validateTabs, setValidateTabs],
  );

  // - Valid Tabs: Form has determined tab content to be valid
  const [validTabs, setValidTabs] = useState<OverlappingDialogTabs[]>([
    OverlappingDialogTabs.BasicData,
  ]);
  const updateValidTabs = useCallback(
    (tab: OverlappingDialogTabs, isValid: boolean) => {
      if (isValid && !validTabs.includes(tab)) {
        setValidTabs([...validTabs, tab]);
      } else if (!isValid && validTabs.includes(tab)) {
        setValidTabs(validTabs.filter((t) => t !== tab));
      }
    },
    [validTabs, setValidTabs],
  );

  const resetTabs = useCallback(() => {
    setActiveTab(OverlappingDialogTabs.BasicData);
    setEnabledTabs([OverlappingDialogTabs.BasicData]);
    setValidateTabs([]);
    setValidTabs([]);
  }, []);

  // Checks & Utils
  const isTabEnabled = useCallback(
    (tab: OverlappingDialogTabs) => {
      return enabledTabs.includes(tab);
    },
    [enabledTabs],
  );

  const isTabInvalid = useCallback(
    (tab: OverlappingDialogTabs) => {
      return validateTabs.includes(tab) && !validTabs.includes(tab);
    },
    [validateTabs, validTabs],
  );

  const getNextTab = useCallback((tab: OverlappingDialogTabs) => {
    const allTabs = Object.values(OverlappingDialogTabs);
    const currentIndex = allTabs.indexOf(tab);
    return currentIndex < allTabs.length
      ? allTabs[currentIndex + 1]
      : undefined;
  }, []);

  const getPreviousTab = useCallback((tab: OverlappingDialogTabs) => {
    const allTabs = Object.values(OverlappingDialogTabs);
    const currentIndex = allTabs.indexOf(tab);
    return 0 < currentIndex ? allTabs[currentIndex - 1] : undefined;
  }, []);

  // Tab Actions
  const onComplete = useCallback(async () => {
    updateValidateTabs(activeTab);

    return trigger().then((isValid) => {
      updateValidTabs(activeTab, isValid);

      if (isValid) {
        confirmationDialogState.open();
      }
    });
  }, [
    trigger,
    activeTab,
    updateValidateTabs,
    updateValidTabs,
    confirmationDialogState,
  ]);

  const onGoToNextTab = useCallback(async () => {
    updateValidateTabs(activeTab);

    return trigger().then((isValid) => {
      updateValidTabs(activeTab, isValid);

      const next = getNextTab(activeTab);
      if (isValid && !!next) {
        updateEnabledTabs(next);
        setActiveTab(next);
      }
    });
  }, [
    trigger,
    activeTab,
    setActiveTab,
    getNextTab,
    updateValidateTabs,
    updateValidTabs,
    updateEnabledTabs,
  ]);

  const onGoToPreviousTab = useCallback(() => {
    const previous = getPreviousTab(activeTab);
    if (!!previous) {
      setActiveTab(previous);
    }
  }, [activeTab, setActiveTab, getPreviousTab]);

  const onChangeTab = useCallback(
    (tab: OverlappingDialogTabs) => {
      if (isTabEnabled(tab)) {
        setActiveTab(tab);
      }
    },
    [isTabEnabled, setActiveTab],
  );

  // Tab configs
  const { tabConfigs } = useMemo(() => {
    const tabLabels: { [key in OverlappingDialogTabs]: string } = {
      [OverlappingDialogTabs.BasicData]: t('laBasicData'),
      [OverlappingDialogTabs.Contact]: t('laContactData'),
      [OverlappingDialogTabs.CustomData]: t('laCustomData'),
      [OverlappingDialogTabs.EmploymentDetails]: t('laEmplDetail'),
      [OverlappingDialogTabs.Rewarding]: t('laRewarding'),
    };

    const getInstruction = (
      title: string,
      contentKey: string,
    ): InstructionDialogConfig | undefined => {
      const richTextContent = instructionStrings?.[contentKey];

      return richTextContent ? { title, richTextContent } : undefined;
    };

    const getInstructions = (
      tab: OverlappingDialogTabs,
    ): InstructionDialogConfig | undefined => {
      switch (tab) {
        case OverlappingDialogTabs.BasicData:
          return getInstruction(
            t('laNewEmpBasicDataInstr'),
            'INSTRUCTIONS_NEW_EMP_BASIC',
          );
        case OverlappingDialogTabs.Contact:
          return getInstruction(
            t('laNewEmpContactDataInstr'),
            'INSTRUCTIONS_NEW_EMP_CONTACT',
          );
        case OverlappingDialogTabs.CustomData:
          return getInstruction(
            t('laNewEmpCustomDataInstr'),
            'INSTRUCTIONS_NEW_EMP_CUSTOM',
          );
        case OverlappingDialogTabs.EmploymentDetails:
          return getInstruction(
            t('laNewEmpEmplDetailInstr'),
            'INSTRUCTIONS_NEW_EMP_DETAILS',
          );
        case OverlappingDialogTabs.Rewarding:
          return getInstruction(
            t('laNewEmpRewardingInstr'),
            'INSTRUCTIONS_NEW_EMP_REWARDING',
          );
        default:
          return undefined;
      }
    };

    const tabConfigs = Object.values(OverlappingDialogTabs).map(
      (tab, index) => {
        return {
          instruction: getInstructions(tab),
          props: {
            label: tabLabels[tab],
            key: tab,
            value: tab,
            'data-cy': tab,
            id: `simple-tab-${index}`,
            'aria-controls': `simple-tabpanel-${index}`,
          },
        };
      },
    );

    return {
      tabConfigs,
    };
  }, [t, instructionStrings]);

  // Revalidate active tab on form field change
  const tabFormValues = useWatch({
    control,
    name: `overlappingEmployment.${
      activeTab as keyof OverlappingEmploymentFormType
    }`,
  });

  useEffect(() => {
    if (validateTabs.includes(activeTab)) {
      trigger().then((isValid) => {
        updateValidTabs(activeTab, isValid);
      });
    }
  }, [trigger, activeTab, validateTabs, updateValidTabs, tabFormValues]);

  return {
    isLoading,
    activeTab,
    resetTabs,
    onGoToNextTab,
    onGoToPreviousTab,
    onChangeTab,
    onComplete,
    tabConfigs,
    isTabEnabled,
    getNextTab,
    getPreviousTab,
    isTabInvalid,
    confirmationDialogState,
  };
};
